import { useState, useEffect, useRef } from "react";
import Navbar from "../components/navbar";
import Footer from "../components/footer";
import image1 from "../assets/Group 1391.png";
import image2 from "../assets/Group 1392.png";
import image3 from "../assets/Group 1393.png";

/* ─── shared style block (mirrors DigiYatra) ─── */
const sharedStyle = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=Sora:wght@300;400;500;600&display=swap');

  * { scroll-behavior: smooth; }

  .font-display { font-family: 'Playfair Display', serif; }
  .font-body    { font-family: 'Sora', sans-serif; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .animate-fade-up    { animation: fadeUp 0.7s ease both; }
  .animate-fade-up-d1 { animation: fadeUp 0.7s 0.1s ease both; }
  .animate-fade-up-d2 { animation: fadeUp 0.7s 0.25s ease both; }
  .animate-fade-up-d3 { animation: fadeUp 0.7s 0.4s ease both; }

  .card-hover { transition: transform 0.2s ease, box-shadow 0.2s ease; }
  .card-hover:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 32px rgba(0,0,0,0.08);
  }

  .progress-bar { transition: width 0.4s ease; }

  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track  { background: #f1f5f9; }
  ::-webkit-scrollbar-thumb  { background: #cbd5e1; border-radius: 4px; }
`;

/* ─── scroll-reveal hook ─── */
function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
}

function AnimSection({ id, children, className = "" }) {
  const [ref, inView] = useInView();
  return (
    <section
      id={id}
      ref={ref}
      className={`transition-all duration-700 ${
        inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      } ${className}`}
    >
      {children}
    </section>
  );
}

/* ─── section label (identical to DigiYatra) ─── */
function SectionLabel({ number, text }) {
  return (
    <div className="flex items-center gap-3 mb-6">
      <span className="text-xs font-semibold tracking-widest text-slate-400 font-body uppercase">
        {String(number).padStart(2, "0")} · {text}
      </span>
      <div className="h-px flex-1 bg-slate-100" />
    </div>
  );
}

/* ─── main component ─── */
export default function Playground() {
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      setScrollProgress((el.scrollTop / (el.scrollHeight - el.clientHeight)) * 100);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const items = [
    {
      src: image1,
      alt: "AI Project 1",
      tag: "Generative UI",
      title: "Prompt-to-UI Explorer",
      description:
        "An experiment in translating natural language prompts into live UI components — exploring how AI can assist designers in rapid prototyping and ideation.",
    },
    {
      src: image2,
      alt: "AI Project 2",
      tag: "AI Interaction Design",
      title: "Conversational Design System",
      description:
        "Designing the visual language for AI chat interfaces — typography, spacing, and motion that makes machine responses feel human and trustworthy.",
    },
    {
      src: image3,
      alt: "AI Project 3",
      tag: "Human × Machine",
      title: "Co-pilot Dashboard",
      description:
        "A concept dashboard exploring how AI co-pilots surface insights without overwhelming users — balancing automation with intentional human control.",
    },
  ];

  return (
    <div className="font-body bg-slate-50 min-h-screen">
      <style>{sharedStyle}</style>

      {/* scroll progress bar */}
      <div className="fixed top-0 left-0 right-0 h-0.5 bg-slate-100 z-50">
        <div
          className="h-full bg-blue-500 progress-bar"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      <main className="max-w-6xl mx-auto px-6 pt-24 pb-24 space-y-24">

        {/* ── HERO ── */}
        <section className="pt-8">
          <div className="inline-flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-full mb-6 animate-fade-up">
            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
            <span className="text-[11px] font-semibold text-blue-600 font-body tracking-wide">
              AI Explorations & Experiments
            </span>
          </div>

          <h1 className="font-display text-5xl lg:text-6xl text-slate-900 leading-tight mb-6 animate-fade-up-d1">
            Designing with{" "}
            <span className="italic text-slate-500">intelligence.</span>
          </h1>

          <div className="max-w-6xl space-y-4 animate-fade-up-d2">
            <p className="text-slate-600 text-base leading-relaxed font-body">
              This is where I explore the intersection of design and artificial intelligence —
              experimenting with generative interfaces, AI-assisted workflows, and
              the human experience of interacting with intelligent systems.
            </p>

            <p className="text-slate-600 text-base leading-relaxed font-body">
              These are living experiments: rough ideas, refined concepts, and everything in
              between. Curious about my full process? Head over to{" "}
              <span className="font-semibold text-slate-800 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-800 transition-all cursor-pointer">
                Work
              </span>{" "}
              to see how these ideas grow into products.
            </p>
          </div>
        </section>

        {/* ── AI PROJECT ITEMS ── */}
        <AnimSection id="playground-items">
          <div className="flex flex-col gap-5">
            {items.map((item, i) => (
              <div
                key={i}
                className="card-hover bg-white rounded-2xl border border-slate-100 overflow-hidden"
              >
                {/* Card header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-slate-50">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 font-body">
                    Exploration {String(i + 1).padStart(2, "0")}
                  </p>
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-blue-500 font-body bg-blue-50 px-2 py-0.5 rounded-full">
                    {item.tag}
                  </span>
                </div>

                {/* Image */}
                <img
                  src={item.src}
                  alt={item.alt}
                  className="w-full h-auto object-cover"
                />

                {/* Card footer */}
                <div className="px-5 py-4 border-t border-slate-50 space-y-1">
                  <p className="text-sm font-semibold text-slate-800 font-body">
                    {item.title}
                  </p>
                  <p className="text-xs text-slate-500 font-body leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </AnimSection>

      </main>
    </div>
  );
}